import {
  getCourseById,
  getCourses,
  updateCourse,
} from "../../../services/courses/courses.service";
import {
  CourseEntity,
  CourseEntityInput,
} from "../../../services/courses/courses.types";
import { createResource } from "../../../services/resources/resources.service";
import { ResourceType } from "../../../types";
import {
  getExamEvaluations,
  getUploadCheck,
} from "../../../pages/App/HubSubida/contentHub.service";
import type { GameWithTags } from "../../../services/gameQuiz/gameQuiz.types";
import { examCourseErrors, examCourseFromCheck } from "./examCourseLink";
import {
  examClaveErrors,
  examCourseIds,
  pickExamEvaluation,
} from "./examEvaluationLookup";
import {
  isExamQuizType,
  isNormalQuizType,
  YamlImportResult,
} from "./yamlImport";

// SCRUM-1153: a YAML imported with `tipo: normal` + `clave` creates, on save,
// a "Quizzes Emmi" resource for the new quiz and appends it to the course
// identified by `clave`. The course is resolved at import time so a bad key
// fails before anything is created.

export interface ResolvedCourse {
  id: number;
  title: string;
  clave: string | null;
}

export type CourseResolution =
  { ok: true; course: ResolvedCourse } | { ok: false; error: string };

// The combined endpoint paginates in memory; a key or a program+title search
// returns a handful of rows, so one generous page is enough.
const SEARCH_LIMIT = 100;

// Subject token in the key → program-code prefix. Mirror of the subject map in
// emmi-backend src/tools/loadNemLinkings.ts.
const SUBJECT_PROGRAM_PREFIX: Record<string, string> = {
  GRAM: "GRA",
  GRA: "GRA",
  ESP: "ESP",
  MAT: "MAT",
  CN: "CNA",
  CNA: "CNA",
  ENS: "ENS",
  ETI: "ETI",
  GEO: "GEO",
  HIS: "HIS",
};

// Primaria key: optional `P`, grade 1-6, a middle number whose meaning is
// unknown (ignored), and the subject token. E.g. `P5_9_GRAM`.
const PRIMARIA_KEY = /^P?([1-6])_[^_]+_([A-Z]+)$/;

export const courseLabel = (course: ResolvedCourse): string =>
  `#${course.id} — ${course.title}`;

const normalizeKey = (key: string): string => key.trim().toUpperCase();

// Keys may be stored without the level letter (`5_9_GRAM`) while the ticket
// writes `P5_9_GRAM`; only a letter followed by a digit is a level prefix.
const stripLevelPrefix = (key: string): string =>
  key.replace(/^[PK](?=\d)/, "");

const normalizeTitle = (title: string): string =>
  title
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

export const toResolvedCourse = (course: CourseEntity): ResolvedCourse => ({
  id: course.id,
  title: course.title,
  clave: course.clave ?? null,
});

const notFound = (clave: string): CourseResolution => ({
  ok: false,
  error: `No se encontró el curso para la clave "${clave}".`,
});

const ambiguous = (
  clave: string,
  matches: CourseEntity[],
): CourseResolution => ({
  ok: false,
  error: `La clave "${clave}" coincide con más de un curso (${matches
    .map((c) => `#${c.id}`)
    .join(", ")}); no se puede elegir uno.`,
});

// `/courses/with-projects` mixes projects in; they never hold quiz resources.
// `title` matches the course title OR its clave.
export const searchCourses = async ({
  limit = SEARCH_LIMIT,
  ...params
}: {
  id?: number;
  title?: string;
  program_code?: string;
  limit?: number;
}): Promise<CourseEntity[]> => {
  const response = await getCourses({ ...params, limit });
  return (response?.data ?? []).filter((c) => !c.isProject);
};

// Only digits mean a course id; anything else is a title or a clave.
const COURSE_ID_QUERY = /^(\d+)$/;

// Picker search: title or clave, plus the course id when the text is a number
// (only digits, e.g. "742"). Titles often hold numbers too, so both searches run and
// the id match goes first.
export const searchCoursesForPicker = async (
  text: string,
  limit: number,
): Promise<CourseEntity[]> => {
  const query = text.trim();
  const id = COURSE_ID_QUERY.exec(query)?.[1];
  if (!id) return searchCourses({ title: query, limit });

  const [byId, byText] = await Promise.all([
    searchCourses({ id: Number(id), limit }),
    searchCourses({ title: id, limit }),
  ]);
  const seen = new Set(byId.map((c) => c.id));
  return [...byId, ...byText.filter((c) => !seen.has(c.id))];
};

const pickUnique = (
  clave: string,
  matches: CourseEntity[],
): CourseResolution | null => {
  if (matches.length === 1)
    return { ok: true, course: toResolvedCourse(matches[0]) };
  if (matches.length > 1) return ambiguous(clave, matches);
  return null;
};

export async function resolveCourseForQuiz(
  rawClave: string,
  quizTitle: string,
): Promise<CourseResolution> {
  const clave = rawClave.trim();
  const key = normalizeKey(clave);
  const bareKey = stripLevelPrefix(key);

  try {
    // 0. Only digits ("742") means a course id: claves always carry a
    // subject token, so there is nothing else to try.
    const courseId = COURSE_ID_QUERY.exec(clave)?.[1];
    if (courseId) {
      const [byId] = await searchCourses({ id: Number(courseId), limit: 1 });
      return byId
        ? { ok: true, course: toResolvedCourse(byId) }
        : { ok: false, error: `No se encontró el curso con ID ${courseId}.` };
    }

    // 1. By `Course.clave`. The backend `title` filter also matches the key
    // with `contains`, so searching the bare key finds both spellings.
    const byKey = (await searchCourses({ title: bareKey })).filter(
      (c) => !!c.clave,
    );
    const exact = byKey.filter((c) => normalizeKey(c.clave!) === key);
    const tolerant = byKey.filter(
      (c) => stripLevelPrefix(normalizeKey(c.clave!)) === bareKey,
    );
    const byClave = pickUnique(clave, exact) ?? pickUnique(clave, tolerant);
    if (byClave) return byClave;

    // 2. Primaria fallback: program code from grade + subject, then the
    // course of that program titled like the quiz. The title is compared
    // here, not sent to the API: its `contains` filter is accent-sensitive.
    const primaria = PRIMARIA_KEY.exec(key);
    const prefix = primaria ? SUBJECT_PROGRAM_PREFIX[primaria[2]] : undefined;
    if (!primaria || !prefix) return notFound(clave);

    const title = normalizeTitle(quizTitle);
    const byTitle = (
      await searchCourses({ program_code: `${prefix}${primaria[1]}P` })
    ).filter((c) => normalizeTitle(c.title) === title);
    return pickUnique(clave, byTitle) ?? notFound(clave);
  } catch {
    return {
      ok: false,
      error: `No se pudo buscar el curso para la clave "${clave}". Inténtalo de nuevo.`,
    };
  }
}

export async function examCourseProblem(
  courseId: number,
  games: GameWithTags[],
): Promise<string | null> {
  try {
    const errors = examCourseErrors(
      examCourseFromCheck(await getUploadCheck(courseId)),
      games,
    );
    return errors.length > 0 ? errors.join(" ") : null;
  } catch {
    return "No se pudo comprobar la evaluación trimestral. Inténtalo de nuevo.";
  }
}

export async function resolveExamCourseForQuiz(
  clave: string,
  quizTitle: string,
  games: GameWithTags[],
  questionClaves: string[] = [],
): Promise<CourseResolution> {
  const courseIds = examCourseIds(games);
  if (courseIds.length === 0) {
    return {
      ok: false,
      error: "El examen no tiene preguntas con el curso que evalúan.",
    };
  }
  let found;
  try {
    found = await getExamEvaluations(courseIds);
  } catch {
    return {
      ok: false,
      error:
        "No se pudo buscar la evaluación trimestral del examen. Inténtalo de nuevo.",
    };
  }
  const claveErrors = examClaveErrors(games, questionClaves, found.courses);
  if (claveErrors.length > 0) {
    return { ok: false, error: claveErrors.join(" ") };
  }
  const resolution = clave.trim()
    ? await resolveCourseForQuiz(clave, quizTitle)
    : pickExamEvaluation(found, courseIds);
  if (!resolution.ok) return resolution;
  const problem = await examCourseProblem(resolution.course.id, games);
  return problem ? { ok: false, error: problem } : resolution;
}

export type CourseLookup =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "resolved"; course: ResolvedCourse }
  | { status: "error"; error: string };

// What an import hands to the form's "Curso" field: the course to preselect
// (null when the clave could not be resolved) and why it is empty.
export interface ImportedCourseField {
  course: ResolvedCourse | null;
  warning: string | null;
}

export interface CourseImportDecision {
  // `tipo: normal` in a form that assigns courses (create mode).
  needsCourse: boolean;
  canImport: boolean;
  // null hides the field: no assignment for this import.
  courseField: ImportedCourseField | null;
}

// An unresolved clave no longer blocks the import: the field starts empty with
// the reason and the form requires a course before saving. YAML errors
// (including `tipo: normal` without `clave`) still block.
export function decideCourseImport(
  result: YamlImportResult | null,
  lookup: CourseLookup,
  assignsCourse: boolean,
): CourseImportDecision {
  const parsedOk = !!result && result.errors.length === 0;
  const needsCourse =
    parsedOk &&
    assignsCourse &&
    (isNormalQuizType(result.tipo) || isExamQuizType(result.tipo));
  if (!needsCourse) {
    return { needsCourse, canImport: parsedOk, courseField: null };
  }
  if (lookup.status === "resolved") {
    return {
      needsCourse,
      canImport: true,
      courseField: { course: lookup.course, warning: null },
    };
  }
  if (lookup.status === "error") {
    return {
      needsCourse,
      canImport: true,
      courseField: { course: null, warning: lookup.error },
    };
  }
  return { needsCourse, canImport: false, courseField: null };
}

export type QuizAssignmentOutcome =
  | { ok: true; resourceId: number; message: string }
  | { ok: false; error: string; resourceId?: number };

// Runs after the quiz exists. No rollback: on failure the message names the
// step that failed and what was already created.
export async function createQuizResourceAndAssign({
  quizId,
  quizTitle,
  course,
}: {
  quizId: number;
  quizTitle: string;
  course: ResolvedCourse;
}): Promise<QuizAssignmentOutcome> {
  let resourceId: number | undefined;
  try {
    const resource = await createResource({
      title: quizTitle,
      type: ResourceType.GAME_QUIZ,
      game_quiz_id: quizId,
      // The backend fills the default cover for GAME_QUIZ.
      cover: "",
      additional_info: "",
      files: [],
    });
    resourceId = resource?.id;
  } catch {
    resourceId = undefined;
  }
  if (!resourceId) {
    return {
      ok: false,
      error: "El quiz se creó, pero no se pudo crear el recurso.",
    };
  }

  const assignError = `El quiz y el recurso #${resourceId} se crearon, pero no se pudo asignar al curso ${courseLabel(course)}.`;
  try {
    // PATCH replaces the whole `course_resources` list (missing rows are
    // soft-deleted), so the current rows go back untouched plus the new one.
    const current = await getCourseById(course.id);
    if (!current) return { ok: false, resourceId, error: assignError };

    const existing = (current.course_resources ?? []).map(
      ({ resource_id, order_by }) => ({ resource_id, order_by }),
    );
    // The backend PATCH is partial (UpdateCourseDto = PartialType): sending
    // only `course_resources` leaves every other course field untouched.
    // max + 1 rather than length: existing order_by values may have gaps.
    const nextOrder = Math.max(-1, ...existing.map((r) => r.order_by ?? 0)) + 1;
    const updated = await updateCourse(course.id, {
      course_resources: [
        ...existing,
        { resource_id: resourceId, order_by: nextOrder },
      ],
    } as CourseEntityInput);
    if (!updated) return { ok: false, resourceId, error: assignError };
  } catch {
    return { ok: false, resourceId, error: assignError };
  }

  return {
    ok: true,
    resourceId,
    message: `Recurso #${resourceId} asignado al curso ${courseLabel(course)}.`,
  };
}
